import { useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';

import { Users } from 'lucide-react';

import { AudienceTab, DEMOGRAPHIC_DIMENSIONS } from '@/components/performance/tabs/audience-tab';
import type { DemographicDimension } from '@/lib/apis';
import { DataTable } from '@/components/glitch-ui/data-table';
import { EmptyState } from '@/components/glitch-ui/empty-state';
import { platformsInScope, type Platform, type PlatformScope } from '@/lib/performance/types';
import { formatMoney, toDisplayAmount } from '@/lib/performance/money';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { SubViewSwitcher } from './sub-view-switcher';
import { useScopedCampaigns } from '@/hooks/usePerformanceData';
import { resolveDateWindow } from '@/lib/performance/date-window';
import { metaIntegrationStatusQueryOptions, metaPlacementQueryOptions } from '@/lib/query-options';
import type { MetaPlacementRow } from '@/lib/types';
import {
  consolidatedPerformanceDateRangeAtom,
  consolidatedPerformanceCustomRangeAtom,
  consolidatedPerformanceCampaignTypesAtom,
  consolidatedPerformanceCampaignIdsAtom,
  consolidatedPerformanceIncludeExternalAtom,
} from '@/store';

type SubViewId = 'geography' | 'device' | 'demographics' | 'placement';

const GOOGLE_SUBVIEWS: { id: Exclude<SubViewId, 'placement'>; label: string }[] = [
  { id: 'geography', label: 'Geography' },
  { id: 'device', label: 'Device' },
  { id: 'demographics', label: 'Demographics' },
];

// Prettify Meta publisher_platform values for display.
const PLACEMENT_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  audience_network: 'Audience Network',
  messenger: 'Messenger',
  unknown: 'Unknown',
};

/** Meta Placement sub-view — a live publisher_platform breakdown (#6049). */
function PlacementSubView({ cs, dateRange }: { cs: string; dateRange: { start_date: string; end_date: string } }) {
  const { data, isLoading } = useQuery(metaPlacementQueryOptions(true, dateRange.start_date, dateRange.end_date));
  const rows = data?.rows ?? [];
  const columns = useMemo<ColumnDef<MetaPlacementRow, unknown>[]>(
    () => [
      { accessorKey: 'value', header: 'Placement', cell: ({ row }) => (
        <span className="font-medium text-gray-900">{PLACEMENT_LABELS[row.original.value] ?? row.original.value}</span>
      ) },
      { accessorKey: 'impressions', header: 'Impr.', cell: ({ row }) => row.original.impressions.toLocaleString() },
      { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.clicks.toLocaleString() },
      { accessorKey: 'leads', header: 'Leads', cell: ({ row }) => row.original.leads.toLocaleString() },
      { accessorKey: 'spend_cents', header: 'Cost', cell: ({ row }) => formatMoney(toDisplayAmount(row.original.spend_cents, 'meta'), cs) },
      { id: 'cpl', accessorFn: (r) => r.cpl_cents ?? null, header: 'Cost / lead', cell: ({ row }) => (row.original.cpl_cents != null ? formatMoney(toDisplayAmount(row.original.cpl_cents, 'meta'), cs) : '—') },
      { id: 'ctr', accessorFn: (r) => r.ctr_pct ?? null, header: 'CTR', cell: ({ row }) => (row.original.ctr_pct != null ? `${row.original.ctr_pct.toFixed(2)}%` : '—') },
    ],
    [cs],
  );
  if (isLoading) return <div className="flex h-24 items-center justify-center text-sm text-gray-400">Loading…</div>;
  return (
    // variant="bordered" like every other table on the surface — without it
    // the empty state floated in an unframed block (ruling 09-09).
    <DataTable
      data={rows}
      columns={columns}
      pageSize={10}
      variant="bordered"
      emptyState="No placement data yet. Placements appear once Meta records delivery in this period."
    />
  );
}

/**
 * Consolidated Audience tab (#5971, CP-9). Google sub-views (Geography / Device
 * / Demographics) reuse the Phase 1 AudienceTab account reports directly; Meta
 * contributes Placement. Sub-views are platform-native — they appear only when
 * their platform is in scope (Placement under Meta, the rest under Google).
 */
export function AudiencePanel({ scope }: { scope: PlatformScope }) {
  const inScope = platformsInScope(scope);
  const googleInScope = inScope.includes('google');
  const metaInScope = inScope.includes('meta');
  const showPlatformTag = inScope.length > 1;

  const items: { id: SubViewId; label: string; platform: Platform }[] = [
    ...(googleInScope ? GOOGLE_SUBVIEWS.map((s) => ({ ...s, platform: 'google' as const })) : []),
    ...(metaInScope ? [{ id: 'placement' as const, label: 'Placement', platform: 'meta' as const }] : []),
  ];

  const [subView, setSubView] = useState<SubViewId>('geography');
  // Owned here so the Demographics dimension switcher can ride the same row as
  // the sub-view switcher instead of taking one of its own (ruling 09-09).
  const [demographicDimension, setDemographicDimension] = useState<DemographicDimension>('age');
  const active: SubViewId = items.some((i) => i.id === subView) ? subView : items[0]?.id ?? 'geography';

  const { symbol } = useCurrencySymbol('google');
  const currencySymbol = symbol ?? '';
  // Meta money renders in the Meta AD ACCOUNT's currency, not the Google account's (#6163 P2,
  // #6182) — the same sourcing the Meta detail pages use. Falls back to the org default while the
  // status loads or when no account is selected.
  const { data: metaStatus } = useQuery({ ...metaIntegrationStatusQueryOptions(), enabled: metaInScope });
  const { symbol: metaCurrencySymbol } = useCurrencySymbol(undefined, metaStatus?.selected_ad_account?.currency);
  // #6278's rule: an unconnected integration must not render as empty data.
  // The sub-view stays listed (the capability should be discoverable) but says
  // to connect Meta rather than implying a failed sync (ruling 09-09).
  const metaConnected = metaInScope && !!metaStatus?.is_connected;

  // Honor the toolbar filters through the shared scoping + window helpers
  // (#6281): the old private LIVE/PAUSED narrowing was exactly the accidental
  // filter #6128 removed from usePerformanceData — this tab reported on a
  // different campaign set than its siblings — and its private date math
  // diverged from the shared exact-day contract (#6274).
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);
  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);
  const { campaigns, scopedCampaigns, scopedGoogleAdsIds } = useScopedCampaigns({ includeExternal, typeFilter, selectedCampaignIds });
  // Draft campaigns still carry their geo targeting (the light campaigns list
  // eager-loads target_locations), so Geography has something true to show.
  const hasTargetedLocations = scopedCampaigns.some((c) => (c.target_locations ?? []).length > 0);
  const campaignIds = scopedGoogleAdsIds;
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const customRange = useAtomValue(consolidatedPerformanceCustomRangeAtom);
  const dateRange = useMemo(() => {
    const w = resolveDateWindow(selectedDays, customRange);
    return { start_date: w.start_date, end_date: w.end_date };
  }, [selectedDays, customRange]);

  return (
    <div className="space-y-4">
      {/* The shared segmented switcher this pattern was extracted into (09-08). */}
      <div className="flex flex-wrap items-center gap-2">
        <SubViewSwitcher
          items={items.map((item) => ({ ...item, platform: showPlatformTag ? item.platform : undefined }))}
          active={active}
          onChange={setSubView}
        />
        {active === 'demographics' && (
          <div className="ml-auto">
            <SubViewSwitcher
              items={DEMOGRAPHIC_DIMENSIONS.map((d) => ({ id: d.value, label: d.label }))}
              active={demographicDimension}
              onChange={setDemographicDimension}
            />
          </div>
        )}
      </div>
      {active === 'placement' ? (
        metaConnected ? (
          <PlacementSubView cs={metaCurrencySymbol} dateRange={dateRange} />
        ) : (
          <EmptyState
            icon={<Users className="h-10 w-10 text-gray-300" strokeWidth={1} />}
            title="Meta is not connected"
            description="Connect a Meta ad account to see which placements your ads run on."
          />
        )
      ) : campaignIds ? (
        <AudienceTab
          subView={active}
          campaignIds={campaignIds}
          currencySymbol={currencySymbol}
          dateRangeFilter={dateRange}
          campaigns={scopedCampaigns}
          demographicDimension={demographicDimension}
        />
      ) : scopedCampaigns.length > 0 && (active === 'device' || active === 'demographics' || (hasTargetedLocations && active === 'geography')) ? (
        // No Google Ads ids yet, but the sub-views still have their real
        // shape to show: target locations on Geography, the three device types
        // with their bid adjustments on Device, and the dimension picker with
        // an explained empty table on Demographics (ruling 09-09). Reports stay
        // disabled so an absent campaign_ids param can never fall back
        // account-wide (#6275).
        <AudienceTab
          subView={active}
          campaignIds={undefined}
          currencySymbol={currencySymbol}
          dateRangeFilter={dateRange}
          campaigns={scopedCampaigns}
          reportsEnabled={false}
          demographicDimension={demographicDimension}
        />
      ) : campaigns.length === 0 ? (
        // No campaigns at all: nothing was ever measured (the reports are
        // deliberately disabled without ids, #6275) — say what unlocks the
        // page, in the house empty-state card (ruling 09-09).
        <EmptyState
          icon={<Users className="h-10 w-10 text-gray-300" strokeWidth={1} />}
          title="No campaigns yet"
          description="Create a campaign to see audience performance here."
        />
      ) : scopedCampaigns.length === 0 ? (
        // Zero-match filter: an absent campaign_ids param would mean
        // account-wide to the backend (#6275) — show the truth instead.
        <EmptyState
          icon={<Users className="h-10 w-10 text-gray-300" strokeWidth={1} />}
          title="No matching campaigns"
          description="No campaigns match the current filters. Adjust the campaign or type filters to see audience data."
        />
      ) : (
        // Campaigns are in scope but none carry a Google Ads id yet: they
        // exist as drafts and have never served, so there is nothing to
        // report — a different truth from "your filters excluded them".
        <EmptyState
          icon={<Users className="h-10 w-10 text-gray-300" strokeWidth={1} />}
          title="Campaigns are not live yet"
          description="Audience data appears once a campaign goes live on Google Ads."
        />
      )}
    </div>
  );
}
